import type {
  SuperAdminOrganizationDetail,
  UpdateOrganizationEntitlementRequest,
} from "@multi-chat/shared";
import { formatStorageGigabyteAmount } from "../settings/format-storage-gigabytes.js";

const DECIMAL_GIGABYTE = 1_000_000_000n;

export interface EntitlementDraft {
  contactsUnlimited: boolean;
  contactsValue: string;
  teamUnlimited: boolean;
  teamValue: string;
  storageUnlimited: boolean;
  storageValue: string;
  storageTouched: boolean;
  whatsappEnabled: boolean;
  facebookEnabled: boolean;
  instagramEnabled: boolean;
}

export function entitlementDraftFromDetail(
  organization: SuperAdminOrganizationDetail,
): EntitlementDraft {
  return {
    contactsUnlimited: organization.usage.contacts.limit === null,
    contactsValue:
      organization.usage.contacts.limit === null ? "" : String(organization.usage.contacts.limit),
    teamUnlimited: organization.usage.team.limit === null,
    teamValue: organization.usage.team.limit === null ? "" : String(organization.usage.team.limit),
    storageUnlimited: organization.usage.storage.limitBytes === null,
    storageValue:
      organization.usage.storage.limitBytes === null
        ? ""
        : formatStorageGigabyteAmount(BigInt(organization.usage.storage.limitBytes)),
    storageTouched: false,
    whatsappEnabled: organization.platformEntitlements.whatsappEnabled,
    facebookEnabled: organization.platformEntitlements.facebookEnabled,
    instagramEnabled: organization.platformEntitlements.instagramEnabled,
  };
}

export function entitlementFormKey(organization: SuperAdminOrganizationDetail): string {
  return [
    organization.id,
    organization.usage.contacts.limit ?? "unlimited",
    organization.usage.team.limit ?? "unlimited",
    organization.usage.storage.limitBytes ?? "unlimited",
    organization.platformEntitlements.whatsappEnabled,
    organization.platformEntitlements.facebookEnabled,
    organization.platformEntitlements.instagramEnabled,
  ].join(":");
}

/** Whole number, or one decimal place. Returns the byte string the entitlement API stores. */
export function parseQuotaGigabytes(value: string): string | null {
  const trimmed = value.trim();
  if (!/^(?:0|[1-9]\d*)(?:\.\d)?$/.test(trimmed)) {
    return null;
  }
  const [whole, fraction = ""] = trimmed.split(".");
  const bytes =
    BigInt(whole ?? "0") * DECIMAL_GIGABYTE +
    (fraction === "" ? 0n : BigInt(fraction) * (DECIMAL_GIGABYTE / 10n));
  return bytes.toString();
}

function parseFiniteCount(value: string, min: number): number | null {
  const trimmed = value.trim();
  if (!/^(?:0|[1-9]\d*)$/.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < min) {
    return null;
  }
  return parsed;
}

export function readEntitlementDraft(
  organization: SuperAdminOrganizationDetail,
  draft: EntitlementDraft,
): { ok: true; patch: UpdateOrganizationEntitlementRequest } | { ok: false } {
  const patch: UpdateOrganizationEntitlementRequest = {};

  const contacts = draft.contactsUnlimited ? null : parseFiniteCount(draft.contactsValue, 0);
  if (!draft.contactsUnlimited && contacts === null) {
    return { ok: false };
  }
  if (contacts !== organization.usage.contacts.limit) {
    patch.contactsLimit = contacts;
  }

  const team = draft.teamUnlimited ? null : parseFiniteCount(draft.teamValue, 1);
  if (!draft.teamUnlimited && team === null) {
    return { ok: false };
  }
  if (team !== organization.usage.team.limit) {
    patch.teamMembersLimit = team;
  }

  if (draft.storageUnlimited) {
    if (organization.usage.storage.limitBytes !== null) {
      patch.storageQuotaBytes = null;
    }
  } else if (organization.usage.storage.limitBytes === null || draft.storageTouched) {
    const bytes = parseQuotaGigabytes(draft.storageValue);
    if (bytes === null) {
      return { ok: false };
    }
    if (bytes !== organization.usage.storage.limitBytes) {
      patch.storageQuotaBytes = bytes;
    }
  }

  if (draft.whatsappEnabled !== organization.platformEntitlements.whatsappEnabled) {
    patch.whatsappEnabled = draft.whatsappEnabled;
  }
  if (draft.facebookEnabled !== organization.platformEntitlements.facebookEnabled) {
    patch.facebookEnabled = draft.facebookEnabled;
  }
  if (draft.instagramEnabled !== organization.platformEntitlements.instagramEnabled) {
    patch.instagramEnabled = draft.instagramEnabled;
  }

  return { ok: true, patch };
}
